import { Contract } from '@ethersproject/contracts'
import { JsonRpcProvider } from '@ethersproject/providers'
import { useMemo } from 'react'

// Multiple RPC endpoints per chain for load balancing and failover
export const PUBLIC_RPCS: { [chainId: number]: string[] } = {
  137: [
    'https://polygon-rpc.com',
    'https://rpc-mainnet.matic.quiknode.pro',
    'https://polygon.drpc.org'
  ],
  56: [
    'https://bsc-dataseed.binance.org',
    'https://bsc-dataseed1.binance.org',
    'https://bsc-dataseed2.binance.org',
    'https://bsc.drpc.org'
  ],
  42161: ['https://arb1.arbitrum.io/rpc', 'https://arbitrum.drpc.org']
}

export const CHAIN_NAMES: { [chainId: number]: string } = {
  137: 'polygon',
  56: 'bnb',
  42161: 'arbitrum'
}

// Track RPC index for round-robin load balancing
const rpcIndexes: { [chainId: number]: number } = {}

// Get next RPC URL using round-robin
function getNextRpcUrl(chainId: number): string | undefined {
  const rpcs = PUBLIC_RPCS[chainId]
  if (!rpcs || rpcs.length === 0) return undefined

  if (rpcIndexes[chainId] === undefined) {
    rpcIndexes[chainId] = 0
  }

  const url = rpcs[rpcIndexes[chainId] % rpcs.length]
  rpcIndexes[chainId] = (rpcIndexes[chainId] + 1) % rpcs.length
  return url
}

// Create provider for specific RPC URL
function createProvider(chainId: number, rpcUrl: string): JsonRpcProvider {
  return new JsonRpcProvider(rpcUrl, chainId)
}

// Get primary provider (uses first RPC)
export function getProvider(chainId: number): JsonRpcProvider | undefined {
  const rpcs = PUBLIC_RPCS[chainId]
  if (!rpcs || rpcs.length === 0) return undefined
  return createProvider(chainId, rpcs[0])
}

// Retry configuration
const DEFAULT_MAX_RETRIES = 3
const DEFAULT_BASE_DELAY_MS = 1000

// Sleep helper
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms))

// Execute with retry and load balancing
export async function executeWithRetry<T>(
  chainId: number,
  operation: (_provider: JsonRpcProvider) => Promise<T>,
  options?: { maxRetries?: number; baseDelayMs?: number }
): Promise<T> {
  const maxRetries = options?.maxRetries ?? DEFAULT_MAX_RETRIES
  const baseDelayMs = options?.baseDelayMs ?? DEFAULT_BASE_DELAY_MS
  const rpcs = PUBLIC_RPCS[chainId]

  if (!rpcs || rpcs.length === 0) {
    throw new Error(`No RPC endpoints configured for chain ${chainId}`)
  }

  let lastError: Error | undefined

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    // Try each RPC in round-robin fashion
    const rpcUrl = getNextRpcUrl(chainId)!
    const provider = createProvider(chainId, rpcUrl)

    try {
      return await operation(provider)
    } catch (err) {
      lastError = err instanceof Error ? err : new Error(String(err))
      console.warn(
        `RPC call failed (attempt ${attempt + 1}/${maxRetries}) on ${rpcUrl}:`,
        lastError.message
      )

      // Exponential backoff before retry
      if (attempt < maxRetries - 1) {
        const delay = baseDelayMs * Math.pow(2, attempt)
        await sleep(delay)
      }
    }
  }

  throw lastError || new Error('All RPC attempts failed')
}

// Batch execute multiple operations with retry
export async function batchExecuteWithRetry<T>(
  chainId: number,
  operations: Array<(provider: JsonRpcProvider) => Promise<T>>,
  options?: { maxRetries?: number; baseDelayMs?: number }
): Promise<T[]> {
  const rpcs = PUBLIC_RPCS[chainId]
  if (!rpcs || rpcs.length === 0) {
    throw new Error(`No RPC endpoints configured for chain ${chainId}`)
  }

  // Execute all operations with the same provider for consistency
  return executeWithRetry(
    chainId,
    async provider => {
      return Promise.all(operations.map(op => op(provider)))
    },
    options
  )
}

export function useMultiChainProviders(): { [chainId: number]: JsonRpcProvider } {
  return useMemo(() => {
    const providers: { [chainId: number]: JsonRpcProvider } = {}
    for (const chainId of Object.keys(PUBLIC_RPCS)) {
      const id = parseInt(chainId)
      const provider = getProvider(id)
      if (provider) {
        providers[id] = provider
      }
    }
    return providers
  }, [])
}

export function useMultiChainContract(
  address: string,
  abi: any[],
  chainId: number
): Contract | undefined {
  return useMemo(() => {
    const provider = getProvider(chainId)
    if (!provider || !address) return undefined
    return new Contract(address, abi, provider)
  }, [address, abi, chainId])
}

// ERC20 ABI for balanceOf and totalSupply
export const ERC20_ABI = [
  'function balanceOf(address owner) view returns (uint256)',
  'function totalSupply() view returns (uint256)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)'
]

// UniswapV2 Pair ABI
export const PAIR_ABI = [
  'function getReserves() view returns (uint112 reserve0, uint112 reserve1, uint32 blockTimestampLast)',
  'function token0() view returns (address)',
  'function token1() view returns (address)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address owner) view returns (uint256)',
  'function decimals() view returns (uint8)'
]
